import {
    Box,
    Typography,
    TextField,
    IconButton,
    styled,
    CircularProgress,
    InputAdornment,
    Autocomplete,
    FormControl,
    Select,
    InputLabel,
    MenuItem,
    ListSubheader,
    Checkbox,
    Tooltip,
    Skeleton,
} from '@mui/material';
import { Remove, OpenInNew } from '@mui/icons-material';
import { useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useRecoilValue } from 'recoil';
import { isAuthorizedQuery } from 'src/selectors/isAuthorizedQuery';
import { useQueryProductVersion } from 'src/hooks/useQueryProductVersion';
import { useQueryProducts } from 'src/components/ProductSearchPage/hooks/useQueryProducts';
import { useAvailableLocales } from 'src/hooks/useAvailableLocales';
import { config } from 'src/lib/config';
import { DEFAULT_CULTURE } from 'src/constants';
import { isValidPrd } from 'src/utils/productKeyValidation';

export type ProductOptionsMap = Record<string, string>;
export type RequiredOptionsMap = Record<string, boolean>;
type Row = { id: string; key: string; value: string; required: boolean };

export type TargetProductOptionsProps = {
    productKey: string;
    version?: number;
    culture: string;
    onChangeProductKey: (prd: string) => void;
    onChangeVersion: (version?: number) => void;
    onChangeOptions?: (opts: ProductOptionsMap, requiredMap: RequiredOptionsMap) => void;
    onChangeCulture: (culture: string) => void;
    initialOptions?: ProductOptionsMap;
    initialRequiredOptions?: RequiredOptionsMap;
};

const StyledTextField = styled(TextField)(({ theme }) => ({
    '& .MuiInputBase-root.Mui-focused': {
        backgroundColor: theme.palette.custom.lightBlue,
    },
}));

const Card = styled(Box)(({ theme }) => ({
    width: '100%',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(2),
    padding: theme.spacing(3),
    marginTop: theme.spacing(2),
}));

const RowGrid = styled('div')(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: '1fr 1fr auto auto',
    gap: theme.spacing(2),
    alignItems: 'start',
    marginBottom: theme.spacing(2),
}));

const HeaderGrid = styled('div')(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: '1fr 1fr auto 30px',
    gap: theme.spacing(2),
    alignItems: 'center',
    marginBottom: theme.spacing(1),
}));

const StyledInputAdornment = styled(InputAdornment)(({ theme }) => ({
    height: '100%',
    position: 'absolute',
    right: theme.spacing(6),
    top: `calc(50% - ${theme.spacing(3)})`,
}));

const StyledHeaderGrid = styled('div')(({ theme }) => ({
    display: 'grid',
    gridTemplateColumns: '4fr 3fr 2fr',
    gap: theme.spacing(2),
}));

const ClickableTypography = styled(Typography)(({ theme }) => ({
    cursor: 'pointer',
    userSelect: 'none',
    '&:hover': {
        color: theme.palette.primary.main,
    },
}));

const uid = (): string => uuidv4();

const rowsToOptions = (rows: Row[]): { options: ProductOptionsMap; required: RequiredOptionsMap } => {
    const options: ProductOptionsMap = {};
    const required: RequiredOptionsMap = {};

    rows.forEach((row) => {
        const k = row.key.trim();
        const v = row.value.trim();

        if (k && v) {
            options[k] = v;
            required[k] = row.required;
        }
    });

    return { options, required };
};

const isComplete = (r: Row): boolean => r.key.trim().length > 0 && r.value.trim().length > 0;
const isEmptyRow = (r: Row): boolean => r.key.trim() === '' && r.value.trim() === '';

const addEmptyRowIfNeeded = (list: Row[]): Row[] => {
    if (!list.length) {
        return [{
            id: uid(), key: '', value: '', required: false,
        }];
    }
    const last = list[list.length - 1];
    const hasEmptyLast = isEmptyRow(last);
    const allComplete = list.every(isComplete);
    const hasIncompleteBeforeLast = list.slice(0, -1).some((r) => !isComplete(r));

    if (hasEmptyLast && hasIncompleteBeforeLast) {
        return list.slice(0, -1);
    }
    if (!hasEmptyLast && allComplete) {
        return [...list, {
            id: uid(), key: '', value: '', required: false,
        }];
    }
    return list;
};

export const TargetProductOptions = ({
    productKey,
    version,
    culture,
    onChangeProductKey,
    onChangeVersion,
    onChangeOptions,
    onChangeCulture,
    initialOptions,
    initialRequiredOptions,
}: TargetProductOptionsProps): JSX.Element => {
    const accessToken = useRecoilValue(isAuthorizedQuery);

    const [rows, setRows] = useState<Row[]>([]);
    const [autoFillDisabled, setAutoFillDisabled] = useState<boolean>(false);
    const bottomRef = useRef<HTMLDivElement | null>(null);

    const [lastKey, setLastKey] = useState<string>('');
    const [lastVersion, setLastVersion] = useState<number | undefined>(undefined);

    const prdCandidate: string = (productKey ?? '').trim();
    const prdIsValid: boolean = isValidPrd(prdCandidate);

    const [appliedInitial, setAppliedInitial] = useState(false);

    const { groupedLocales } = config as unknown as { groupedLocales: Record<string, string[]> };

    const { data: productResp, isLoading } = useQueryProductVersion(
        accessToken,
        { productKey: prdIsValid ? prdCandidate : '', version },
        { enabled: prdIsValid, suspense: false },
    );

    const { data: productsData, isLoading: isLoadingProductName } = useQueryProducts(
        accessToken,
        { productKey: prdIsValid ? prdCandidate : '' },
        { enabled: prdIsValid, suspense: false },
    );

    const activeProduct = productResp?.result?.find((v) => v.isCurrent);

    const product = version
        ? productResp?.result?.find((v) => v.version === version)
        : activeProduct ?? productResp?.result?.[0];

    const productOptions = product?.options ?? [];
    const activeVersion = product?.version;

    // Use available locales hook to get cultures available for this product
    const { availableLocales } = useAvailableLocales(
        prdIsValid ? prdCandidate : null,
        activeVersion ?? null,
    );

    // Filter locales to only show available ones
    const filteredGroupedLocales = Object.entries(groupedLocales).reduce((acc, [region, locales]) => {
        const filtered = availableLocales.length > 0
            ? locales.filter((locale) => availableLocales.includes(locale))
            : locales;

        if (filtered.length > 0) {
            acc[region] = filtered;
        }
        return acc;
    }, {} as Record<string, string[]>);

    const optionNames: string[] = [];
    const valuesByName: Record<string, string[]> = {};

    useEffect(() => {
        if (productKey.trim() === '') {
            setRows([{
                id: uid(), key: '', value: '', required: false,
            }]);
        }
    }, [productKey]);

    // Set default culture when available locales change
    useEffect(() => {
        if (availableLocales.length > 0 && !availableLocales.includes(culture)) {
            // If DEFAULT_CULTURE is available, select it, otherwise select the first one alphabetically
            if (availableLocales.includes(DEFAULT_CULTURE)) {
                onChangeCulture(DEFAULT_CULTURE);
            } else {
                const sortedLocales = [...availableLocales].sort();

                onChangeCulture(sortedLocales[0]);
            }
        }
    }, [availableLocales, culture, onChangeCulture]);

    useEffect(() => {
        if (appliedInitial) {
            return;
        }
        const entries = Object.entries(initialOptions ?? {});

        if (!entries.length) {
            return;
        }
        setRows(addEmptyRowIfNeeded(entries.map(([key, value]) => ({
            id: uid(),
            key,
            value,
            required: initialRequiredOptions?.[key] ?? false,
        }))));
        setAutoFillDisabled(true);
        setAppliedInitial(true);
    }, [initialOptions, initialRequiredOptions, appliedInitial]);

    for (const opt of productOptions) {
        const option = opt as { name?: string; values?: unknown };
        const name = String(option.name ?? '');

        if (name) {
            optionNames.push(name);
            const rawVals = (option.values ?? []) as any[];
            const vals: string[] = Array.isArray(rawVals)
                ? rawVals.map((v: any) => (typeof v === 'string' ? v : String(v?.name ?? ''))).filter(Boolean)
                : [];

            valuesByName[name] = vals;
        }
    }
    optionNames.sort((a, b) => b.localeCompare(a, undefined, { numeric: true, sensitivity: 'base' }));
    for (const k of optionNames) {
        valuesByName[k] = [...(valuesByName[k] ?? [])].sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }));
    }

    if (prdIsValid && product && (lastKey !== prdCandidate || lastVersion !== product.version) && !autoFillDisabled) {
        const targetProductRows: Row[] = optionNames.map((name) => {
            const vals = valuesByName[name] ?? [];
            const first = vals.length > 0 ? vals[0] : '';

            return {
                id: uid(), key: name, value: first, required: false,
            };
        });

        setRows(targetProductRows);
        setLastKey(prdCandidate);
        setLastVersion(product.version);

        if (!autoFillDisabled && version === undefined && activeVersion != null) {
            onChangeVersion(activeVersion);
        }

        const { options, required } = rowsToOptions(targetProductRows);

        onChangeOptions?.(options, required);
    }

    const applyRowChanges = (next: Row[]): void => {
        const normalized = addEmptyRowIfNeeded(next);
        const grew = normalized.length > rows.length;

        setRows(normalized);
        const { options, required } = rowsToOptions(normalized);

        onChangeOptions?.(options, required);
        if (grew) {
            requestAnimationFrame(() => {
                bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            });
        }
    };

    const updateRowKey = (id: string, key: string): void => {
        const normalized = key.trim().toLowerCase();
        const me = rows.findIndex((r) => r.id === id);

        if (optionNames.length > 0 && rows.some((r, i) => i !== me && r.key.trim().toLowerCase() === normalized)) {
            return;
        }
        const vals = valuesByName[key] ?? [];
        const nextValue = vals.length > 0 ? vals[0] : '';

        applyRowChanges(rows.map((r) => (r.id === id ? {
            ...r, key, value: nextValue, required: r.required,
        } : r)));
    };

    const updateRowValue = (id: string, value: string): void => {
        applyRowChanges(rows.map((r) => (r.id === id ? { ...r, value } : r)));
    };

    const updateRowRequired = (id: string, required: boolean): void => {
        applyRowChanges(rows.map((r) => (r.id === id ? { ...r, required } : r)));
    };

    const removeRow = (id: string): void => {
        const next = rows.filter((r) => r.id !== id);

        applyRowChanges(next.length ? next : [{
            id: uid(), key: '', value: '', required: false,
        }]);
    };

    const handleProductKeyChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const next = e.target.value;

        onChangeProductKey(next);
        onChangeVersion(undefined);
        setLastVersion(undefined);
        setLastKey('');
        setRows([{
            id: uid(), key: '', value: '', required: false,
        }]);
        setAutoFillDisabled(false);
    };

    const handleProductVersionChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const raw = e.target.value.trim();

        if (raw === '') {
            onChangeVersion(undefined);
            setLastVersion(undefined);
            setAutoFillDisabled(true);
            return;
        }

        const parsed = raw === '' ? undefined : Number(raw);

        if (Number.isFinite(parsed)) {
            onChangeVersion(parsed);
            setLastVersion(undefined);
            setAutoFillDisabled(false);
        }
    };

    const toggleAllRequired = (): void => {
        const validRows = rows.filter((r) => !isEmptyRow(r));

        if (validRows.length === 0) return;

        const allChecked = validRows.every((r) => r.required);

        // If all are checked, uncheck all
        // If all are unchecked OR mixed state, check all
        const newRequiredState = !allChecked;

        applyRowChanges(rows.map((r) => ({
            ...r,
            required: (!isEmptyRow(r)) ? newRequiredState : false,
        })));
    };

    return (
        <Card>
            <Typography gutterBottom fontWeight="medium" variant="subtitle1">
                Target Product
            </Typography>
            <StyledHeaderGrid>
                <StyledTextField
                    error={productKey.length > 0 && !prdIsValid}
                    helperText={productKey.length > 0 && !prdIsValid ? 'Invalid Product Key.' : ''}
                    InputProps={{
                        endAdornment: (
                            <StyledInputAdornment position="end">
                                {prdIsValid && isLoading ? <CircularProgress color="inherit" size={18} /> : null}
                            </StyledInputAdornment>
                        ),
                    }}
                    label="Product Key"
                    placeholder="PRD-1234567"
                    size="small"
                    value={productKey}
                    onChange={handleProductKeyChange}
                />
                <TextField
                    disabled={!prdIsValid}
                    label="Product Version"
                    size="small"
                    value={version ?? ''}
                    onBlur={(): void => {
                        setAutoFillDisabled(false);
                    }}
                    onChange={handleProductVersionChange}
                    onFocus={(): void => setAutoFillDisabled(true)}
                />
                <FormControl fullWidth size="small">
                    <InputLabel id="culture-select-label">Culture</InputLabel>
                    <Select
                        disabled={!prdIsValid || !activeVersion}
                        id="culture-select"
                        label="Culture"
                        labelId="culture-select-label"
                        value={culture}
                        onChange={(e): void => onChangeCulture(e.target.value as string)}
                    >
                        {Object.entries(filteredGroupedLocales).flatMap(([region, locales]) => ([
                            <ListSubheader key={region}>{region}</ListSubheader>,
                            ...locales.map((loc) => (
                                <MenuItem key={loc} value={loc}>{loc}</MenuItem>
                            )),
                        ]))}
                    </Select>
                </FormControl>
            </StyledHeaderGrid>
            {prdIsValid && (
                <Box
                    sx={{
                        my: 2, display: 'flex', alignItems: 'center', gap: 1,
                    }}
                >
                    {isLoadingProductName && (
                        <Skeleton animation="wave" height={24} width={200} />
                    )}
                    {!isLoadingProductName && productsData?.result?.[0]?.matching_versions?.[0]?.productName && (
                        <>
                            <Typography color="text.secondary" variant="body2">
                                {productsData.result[0].matching_versions[0].productName}
                            </Typography>
                            <Tooltip title="View in PIM">
                                <IconButton
                                    sx={{
                                        padding: 0.5,
                                        color: 'text.secondary',
                                        width: 16,
                                        height: 16,
                                        '& .MuiSvgIcon-fontSizeSmall': {
                                            fontSize: '0.875rem',
                                        },
                                    }}
                                    onClick={(): void => window.open(
                                        `https://pim.products.cimpress.io/merchant/products/${prdCandidate}/versions/${version ?? activeVersion}/overview`,
                                        '_blank',
                                    )?.focus()}
                                >
                                    <OpenInNew sx={{ fontSize: '16px', color: 'text.secondary' }} />
                                </IconButton>
                            </Tooltip>
                        </>
                    )}
                </Box>
            )}
            <HeaderGrid sx={{ mb: 2 }}>
                <Typography fontWeight="medium" variant="body2">
                    Product Options
                </Typography>
                <Box />
                <Box sx={{
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}
                >
                    <Tooltip
                        arrow
                        placement="top"
                        title="Click to toggle all required checkboxes. Required options cannot be changed when finding content. Uncheck to prefer the option but not require it if necessary to find content"
                    >
                        <ClickableTypography
                            color="text.secondary"
                            variant="caption"
                            onClick={toggleAllRequired}
                        >
                            Required
                        </ClickableTypography>
                    </Tooltip>
                </Box>
                <Box />
            </HeaderGrid>
            {rows.map((r, idx) => {
                const otherUsed = new Set(
                    rows
                        .filter((_, i) => i !== idx)
                        .map((x) => x.key.trim().toLowerCase())
                        .filter(Boolean),
                );

                const nameOptions: string[] = optionNames.filter(
                    (n) => !otherUsed.has(n.trim().toLowerCase()),
                );
                const valueOptions: string[] = valuesByName[r.key] ?? [];

                return (
                    <RowGrid key={r.id}>
                        <Autocomplete
                            freeSolo
                            disabled={!prdIsValid || !activeVersion}
                            options={nameOptions}
                            renderInput={(params): JSX.Element => (
                                <TextField {...params} label="Option Name" placeholder="e.g., Size" size="small" />
                            )}
                            value={r.key}
                            onInputChange={(_, val): void => updateRowKey(r.id, val)}
                        />
                        <Autocomplete
                            freeSolo
                            disabled={!prdIsValid || !activeVersion}
                            options={valueOptions}
                            renderInput={(params): JSX.Element => (
                                <TextField {...params} label="Option Value" placeholder='e.g., 3"x3"' size="small" />
                            )}
                            value={r.value}
                            onInputChange={(_, val): void => updateRowValue(r.id, val)}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Checkbox
                                checked={r.required}
                                disabled={!prdIsValid || !activeVersion}
                                size="small"
                                onChange={(e): void => updateRowRequired(r.id, e.target.checked)}
                            />
                        </Box>
                        <IconButton
                            aria-label="remove row"
                            disabled={!prdIsValid || !activeVersion || (rows.length <= 1 && idx === rows.length - 1)}
                            size="small"
                            onClick={(): void => removeRow(r.id)}
                        >
                            <Remove />
                        </IconButton>
                    </RowGrid>
                );
            })}
            <div ref={bottomRef} />
        </Card>
    );
};
